import type { OperationsReport, OperationsSignal } from '../../types/reports';
import type { Segment } from '../reportsView';
import { formatMoney } from '../ownerOverview';
import { intLabel, moneyLabel, plural, strong, text, type BadgeView, type RowView, type SectionView, type StatView } from './shared';

/**
 * P-084 · R7 «¿Cómo va la operación?» — presentación pura. El servidor ya clasificó (normal / atención / pendiente /
 * no evaluable) y ordenó las señales por precedencia; aquí se escriben. Una anulación autorizada se cuenta, no se
 * juzga; un corte sin esperado no es cero.
 */
export interface OperationsView {
  say: Segment[];
  state: { key: OperationsReport['state']; label: string; tone: BadgeView['tone'] };
  stats: StatView[];
  signals: SectionView;
  detail: SectionView[];
  notes: string[];
  empty: { title: string; description: string } | null;
}

export const OPERATIONS_VOID_NOTE = 'Una anulación autorizada es normal: aquí se cuenta, no se juzga.';
export const OPERATIONS_DISCOUNT_NOTE = '«Ventas sin descontar de inventario» solo cuenta recibos de Loyverse; el punto de venta descuenta al cobrar.';
export const OPERATIONS_EMPTY_TITLE = 'No hubo movimientos en este periodo.';
export const OPERATIONS_EMPTY_TEXT = 'Sin ventas ni cortes registrados no hay nada que revisar aquí.';

const STATE_LABEL: Record<OperationsReport['state'], { label: string; tone: BadgeView['tone'] }> = {
  normal: { label: 'En orden', tone: 'success' },
  attention: { label: 'Atención', tone: 'warning' },
  pending: { label: 'Pendientes', tone: 'info' },
  unknown: { label: 'No evaluable', tone: 'neutral' },
};
const SEVERITY_BADGE: Record<OperationsSignal['severity'], BadgeView> = {
  attention: { text: 'Atención', tone: 'warning' },
  pending: { text: 'Pendiente', tone: 'info' },
  info: { text: 'Informativo', tone: 'neutral' },
  unknown: { text: 'No evaluable', tone: 'neutral' },
};

export function operationsReportView(report: OperationsReport): OperationsView {
  const notes = [OPERATIONS_VOID_NOTE, OPERATIONS_DISCOUNT_NOTE];
  // R2-11: las cancelaciones antes de cobrar y las solicitudes ya resueltas también son actividad del periodo.
  const noActivity = report.shifts.closed === 0 && report.shifts.voided === 0 && report.voids.total.count === 0 && report.voids.canceledBeforePayment === 0 && report.requests.pendingTotal === 0 && report.requests.resolved.approved + report.requests.resolved.rejected === 0 && report.awaitingPayment.count === 0 && report.storesWithoutClose.length === 0 && report.signals.length === 0;
  const state = { key: report.state, ...STATE_LABEL[report.state] };
  if (noActivity) {
    return { say: [text(OPERATIONS_EMPTY_TITLE)], state, stats: statsOf(report), signals: { key: 'signals', title: 'Qué revisar', rows: [], empty: null, note: null, more: null }, detail: [], notes, empty: { title: OPERATIONS_EMPTY_TITLE, description: OPERATIONS_EMPTY_TEXT } };
  }

  const actionable = report.signals.filter((signal) => signal.severity === 'attention' || signal.severity === 'pending');
  const say: Segment[] = [];
  if (actionable.length > 0) {
    say.push(text('Hay '), strong(`${actionable.length} ${plural(actionable.length, 'cosa', 'cosas')} que revisar`), text(`: ${actionable.slice(0, 3).map(inSentence).join('; ')}${actionable.length > 3 ? ` y ${actionable.length - 3} más` : ''}.`));
  } else if (report.state === 'unknown') {
    say.push(text('No pude evaluar toda la operación: '), text(report.signals.filter((signal) => signal.severity === 'unknown').map(inSentence).join('; ')), text('.'));
  } else {
    const cuadraron = report.shifts.closed > 0 && report.shifts.uncomputable === 0;
    say.push(text(cuadraron ? 'La operación va en orden: los cortes cuadraron y no hay nada pendiente.' : 'La operación va en orden: no hay nada pendiente.'));
  }

  const signals: SectionView = {
    key: 'signals',
    title: 'Qué revisar',
    rows: report.signals.map((signal) => ({
      key: signal.key, title: signal.title, subtitle: signal.detail || null, meta: [], value: signal.amount === null ? null : moneyLabel(signal.amount),
      valueTone: signal.severity === 'attention' ? 'danger' : 'default', badge: SEVERITY_BADGE[signal.severity], figures: [], destination: signal.destination,
    })),
    empty: report.signals.length === 0 ? 'Nada que revisar: cortes cuadrados y sin pendientes.' : null,
    note: null,
    more: null,
  };

  const shiftRows: RowView[] = report.shifts.top.map((shift) => ({
    key: shift.id, title: `${shift.folio} · ${shift.storeName}`, subtitle: `Corte del ${shift.dayIso.slice(8, 10)}/${shift.dayIso.slice(5, 7)}`, meta: [],
    value: moneyLabel(shift.cashDifference), valueTone: shift.status === 'SHORT' ? 'danger' : 'warning',
    badge: { text: shift.status === 'SHORT' ? 'Faltante' : 'Sobrante', tone: shift.status === 'SHORT' ? 'danger' : 'warning' },
    figures: [{ label: 'Esperado', value: moneyLabel(shift.expectedCash) }, { label: 'Contado', value: moneyLabel(shift.countedCash) }], destination: 'shifts',
  }));
  const detail: SectionView[] = [
    {
      key: 'shifts', title: 'Cortes con mayor diferencia', rows: shiftRows,
      empty: report.shifts.closed === 0 ? 'No hubo cortes cerrados en el periodo.' : report.shifts.uncomputable === report.shifts.closed ? 'Ningún corte del periodo tiene efectivo esperado: no hay diferencia que calcular.' : 'Todos los cortes calculables cuadraron.',
      note: report.shifts.uncomputable > 0 ? `${report.shifts.uncomputable} ${plural(report.shifts.uncomputable, 'corte', 'cortes')} sin efectivo esperado: no se cuenta como cero.` : null,
      more: null,
    },
    {
      key: 'voids', title: 'Anuladas y canceladas',
      rows: [
        ...report.voids.bySource.map((row) => ({ key: row.source, title: `Ventas del periodo anuladas · ${row.label}`, subtitle: null, meta: [], value: row.amount === null ? null : moneyLabel(row.amount), valueTone: 'default' as const, badge: { text: `${intLabel(row.count)}`, tone: 'neutral' as const }, figures: [], destination: 'other-sales' })),
        { key: 'canceled', title: 'Canceladas antes de cobrar', subtitle: 'Sin efectos: no hubo nada que revertir.', meta: [], value: null, valueTone: 'muted', badge: { text: `${intLabel(report.voids.canceledBeforePayment)}`, tone: 'neutral' }, figures: [], destination: null },
      ],
      empty: null, note: null, more: null,
    },
    {
      key: 'requests', title: 'Solicitudes',
      rows: [
        ...report.requests.pending.map((row) => ({ key: `${row.targetType}:${row.type}`, title: `${row.type === 'MODIFY' ? 'Cambio' : 'Anulación'} · ${row.targetLabel}`, subtitle: 'Por revisar.', meta: [], value: null, valueTone: 'default' as const, badge: { text: `${intLabel(row.count)}`, tone: 'info' as const }, figures: [], destination: 'void-requests' })),
        { key: 'resolved', title: 'Resueltas en el periodo', subtitle: `${intLabel(report.requests.resolved.approved)} ${plural(report.requests.resolved.approved, 'aprobada', 'aprobadas')} · ${intLabel(report.requests.resolved.rejected)} ${plural(report.requests.resolved.rejected, 'rechazada', 'rechazadas')}`, meta: [], value: null, valueTone: 'muted', badge: null, figures: [], destination: 'void-requests' },
      ],
      empty: null, note: null, more: null,
    },
  ];
  if (report.shifts.voided > 0 || report.shifts.withWarnings > 0) {
    detail.push({
      key: 'shift-flags', title: 'Otros avisos de cortes',
      rows: [
        ...(report.shifts.voided > 0 ? [{ key: 'voided', title: 'Cortes anulados', subtitle: 'No cuentan en las diferencias.', meta: [], value: null, valueTone: 'muted' as const, badge: { text: `${intLabel(report.shifts.voided)}`, tone: 'neutral' as const }, figures: [], destination: 'shifts' }] : []),
        ...(report.shifts.withWarnings > 0 ? [{ key: 'warnings', title: 'Cortes con advertencias', subtitle: 'El corte dejó avisos al cerrarse.', meta: [], value: null, valueTone: 'muted' as const, badge: { text: `${intLabel(report.shifts.withWarnings)}`, tone: 'neutral' as const }, figures: [], destination: 'shifts' }] : []),
      ],
      empty: null, note: null, more: null,
    });
  }

  return { say, state, stats: statsOf(report), signals, detail, notes, empty: null };
}

function statsOf(report: OperationsReport): StatView[] {
  const { shifts, voids, requests, awaitingPayment, salesToDiscount } = report;
  const pending = requests.pendingTotal + awaitingPayment.count + (salesToDiscount.available ? salesToDiscount.count : 0);
  const differenceValue = shifts.netDifference === null ? (shifts.closed === 0 ? '—' : 'no calculable') : formatMoney(shifts.netDifference);
  const differenceHint = shifts.netDifference === null ? (shifts.closed === 0 ? null : 'sin efectivo esperado') : Number(shifts.netDifference) < 0 ? 'faltante neto' : Number(shifts.netDifference) > 0 ? 'sobrante neto' : 'cuadrado';
  return [
    { key: 'closed', label: 'Cortes cerrados', value: intLabel(shifts.closed), hint: shifts.short.count + shifts.over.count > 0 ? `${intLabel(shifts.short.count + shifts.over.count)} con diferencia` : shifts.closed > 0 ? 'todos cuadrados' : null, tone: 'default' },
    { key: 'difference', label: 'Diferencia de caja', value: differenceValue, hint: differenceHint, tone: shifts.netDifference !== null && Number(shifts.netDifference) < 0 ? 'danger' : shifts.netDifference !== null && Number(shifts.netDifference) > 0 ? 'warning' : 'default' },
    { key: 'voids', label: 'Ventas anuladas', value: intLabel(voids.total.count), hint: voids.total.count > 0 && voids.total.amount !== null ? formatMoney(voids.total.amount) : null, tone: 'default' },
    { key: 'pending', label: 'Pendientes', value: salesToDiscount.available ? intLabel(pending) : `${intLabel(pending)}+`, hint: salesToDiscount.available ? (pending > 0 ? 'solicitudes, guardadas y sin descontar' : null) : 'una lectura no disponible', tone: pending > 0 ? 'warning' : 'default' },
  ];
}

const lowerFirst = (value: string) => value.charAt(0).toLowerCase() + value.slice(1);
/** El título de una señal dentro de la frase: en minúscula, salvo cuando empieza con el nombre de una tienda (nombre propio). */
const inSentence = (signal: OperationsSignal) => (signal.key === 'stores-without-close' && signal.count === 1 ? signal.title : lowerFirst(signal.title));
